import classNames from 'classnames'
import { useAtom } from 'jotai'
import { MyForm } from '~/components/ui/MyForm'
import {
  cfgAtom,
  clipSkipAtom,
  ensdAtom,
  modelCodeAtom,
  selectSamplerAtom,
} from './atom'
import styles from '../AiForm.module.css'
import { useEffect, useMemo, useState } from 'react'
import { MyIcon } from '~/components/ui/MyIcon'
import { isNullOrUndefined, uniqueFunc } from '~/utils'
import { AiCreateArtworkModel } from '~/graphqls/types'
import { OptionsTabs } from '../OptionsTabs'
import { ModelThemeItem, ModelThemeOptionsProps } from './ModelThemeComp'
import useOptions from './useOptions'
import { MyBtn } from '~/components/ui/MyBtn'
import { MyModal } from '~/components/ui/MyModal'

/** 默认显示数量 */
const DEFAULT_SHOW_COUNT = 14

export const LabModelCodeComp = () => {
  const [modelCode, setModelCode] = useAtom(modelCodeAtom)
  const [, setSelectSampler] = useAtom(selectSamplerAtom)
  const [, setCfg] = useAtom(cfgAtom)
  const [, setEnsd] = useAtom(ensdAtom)
  const [, setClipSkip] = useAtom(clipSkipAtom)
  const [, setCurrentIndex] = useState(0)
  const { modelThemeOptions } = useOptions()
  const [visible, setVisible] = useState(false)

  const [selectModel, setSelectModel] = useState<AiCreateArtworkModel>(
    AiCreateArtworkModel.STABLE_DIFFUSION
  )

  const handleClickOption = (item: ModelThemeOptionsProps) => {
    setModelCode(item.modelCode!)
    setCfg(item.cfg ?? undefined)
    setSelectSampler(
      isNullOrUndefined(item.samplerIndex) ? undefined : [item.samplerIndex!]
    )
    setEnsd(item.ensd ?? undefined)
    setClipSkip(item.clipSkip ?? undefined)
  }

  const tabs = uniqueFunc(
    modelThemeOptions.map(it => ({
      name: it.category,
      modelCode: it.modelCode,
      model: it.model,
    })),
    'model'
  )

  const curModelThemeOptions = useMemo(() => {
    return modelThemeOptions.filter(it => it.model === selectModel)
  }, [modelThemeOptions, selectModel])

  const showMore =
    curModelThemeOptions?.length > DEFAULT_SHOW_COUNT &&
    selectModel !== AiCreateArtworkModel.PRETEND_MJ

  const defaultOptions = uniqueFunc(
    [
      ...curModelThemeOptions?.slice(0, DEFAULT_SHOW_COUNT),
      ...curModelThemeOptions.filter(o => o.modelCode === modelCode),
    ],
    'name'
  )

  /** 默认选中第一个模型主题 */
  useEffect(() => {
    if (
      curModelThemeOptions?.findIndex(item => item.modelCode === modelCode) ===
      -1
    ) {
      setModelCode(curModelThemeOptions?.[0].modelCode!)

      setCfg(curModelThemeOptions?.[0]?.cfg ?? undefined)
      setSelectSampler(
        isNullOrUndefined(curModelThemeOptions?.[0]?.samplerIndex)
          ? undefined
          : [curModelThemeOptions?.[0]?.samplerIndex!]
      )
      setEnsd(curModelThemeOptions?.[0]?.ensd ?? undefined)
      setClipSkip(curModelThemeOptions?.[0]?.clipSkip ?? undefined)
    }
  }, [curModelThemeOptions])

  useEffect(() => {
    setSelectModel(
      modelThemeOptions.filter(it => it.modelCode === modelCode)[0]?.model ||
        AiCreateArtworkModel.STABLE_DIFFUSION
    )
  }, [modelThemeOptions, modelCode])

  return (
    <>
      <MyForm.Item
        // label="* 模型主题"
        className={classNames(styles.borderLessFormItem, '!mb-8')}
      >
        <OptionsTabs
          options={tabs.map(it => ({ label: it.name }))}
          currentIndex={tabs.findIndex(item => item.model === selectModel)}
          setCurrentIndex={setCurrentIndex}
          onChange={index => {
            setSelectModel(tabs[index].model as AiCreateArtworkModel)
          }}
        />

        <div className="flex flex-wrap gap-3">
          {defaultOptions.map(item => {
            const isActive = modelCode === item.modelCode
            const isLarge = selectModel === AiCreateArtworkModel.MIDJOURNEY
            return (
              <ModelThemeItem
                key={`${item.name}_${item.modelCode}`}
                isActive={isActive}
                size={isLarge ? 'large' : 'middle'}
                item={item as ModelThemeOptionsProps}
                onChecked={() =>
                  handleClickOption(item as ModelThemeOptionsProps)
                }
              />
            )
          })}
          {showMore && (
            <div
              className={classNames(
                'h-16 mb-3 rounded-lg text-neutral-800 font-semibold border-2 border-neutral-300 cursor-pointer overflow-hidden flex items-center justify-center'
              )}
              style={{
                background: 'rgba(35, 38, 47, 0.32)',
                width: 'calc((100% - 48px) / 5)',
              }}
              onClick={() => setVisible(true)}
            >
              <MyIcon
                className="text-[14px] leading-[16px] font-normal"
                icon={'more1'}
              />
              <span className="ml-0.5">更多</span>
            </div>
          )}
        </div>
      </MyForm.Item>
      <MyModal
        open={visible}
        title={<>模型主题</>}
        onCancel={() => setVisible(false)}
        maskStyle={{
          backgroundColor: 'rgba(0,0,0,0.8)',
        }}
        dark
        width={1440}
        footer={null}
        centered
        isFullScreen
      >
        <OptionsTabs
          options={tabs.map(it => ({ label: it.name }))}
          currentIndex={tabs.findIndex(item => item.model === selectModel)}
          setCurrentIndex={setCurrentIndex}
          onChange={index => {
            setSelectModel(tabs[index].model as AiCreateArtworkModel)
          }}
        />
        <div
          className="flex content-start flex-wrap mb-3 gap-3 min-h-[100px] overflow-auto scrollbar"
          style={{
            height: `calc(100vh - 216px)`, // 216px为其他元素的高度+间距
          }}
        >
          {curModelThemeOptions.map(item => {
            const isActive = modelCode === item.modelCode
            const isLarge = selectModel === AiCreateArtworkModel.PRETEND_MJ
            return (
              <ModelThemeItem
                key={`${item.name}_${item.modelCode}`}
                isActive={isActive}
                size={isLarge ? 'large' : 'small'}
                item={item}
                onChecked={() => handleClickOption(item)}
              />
            )
          })}
        </div>
        <div className="flex justify-between items-center gap-3">
          <MyBtn
            block
            type="default"
            className="!bg-neutral-200 !border-neutral-200 !text-neutral-800"
            onClick={() => {
              setVisible(false)
            }}
          >
            取消
          </MyBtn>
          <MyBtn block type="primary" onClick={() => setVisible(false)}>
            确定
          </MyBtn>
        </div>
      </MyModal>
    </>
  )
}
